import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { ArrowUpRight, MessageCircle, FileText, CalendarClock } from 'lucide-react';
import { setLeadSource } from '@/lib/leadSource';

/**
 * SectionActions — mini, calm, contextual CTA row.
 *
 * Designed to live at the end of marketing sections without competing with
 * the page's primary hero CTA. Three quiet text-links with tiny icons —
 * never pill buttons, never full-width blocks.
 *
 * Props
 *  - label?: small intro text on the left (e.g. "Vuoi saperne di più?")
 *  - source: lead-source attribution prefix (e.g. "about", "why", "target")
 *  - items?: subset of ['whatsapp','brochure','demo'] (default: all three)
 *  - align?: 'start' | 'between' (default: 'start')
 */
export const SectionActions = ({
  label,
  source = 'section',
  items = ['whatsapp', 'brochure', 'demo'],
  align = 'start',
}) => {
  const { locale } = useLanguage();
  const set = items instanceof Set ? items : new Set(items);

  const txt = locale === 'en'
    ? { whatsapp: 'Message us on WhatsApp', brochure: 'Download the brochure', demo: 'See Prime Vision in action' }
    : { whatsapp: 'Scrivici su WhatsApp', brochure: 'Scarica la brochure', demo: 'Vedi Prime Vision in azione' };

  const waMsg = locale === 'en'
    ? 'Hi!%20I%27d%20love%20to%20know%20more%20about%20Prime%20Vision.'
    : 'Ciao!%20Vorrei%20avere%20maggiori%20informazioni%20su%20Prime%20Vision.';

  return (
    <div
      data-testid={`pv-section-actions-${source}`}
      className={`flex flex-wrap items-center gap-x-6 gap-y-3 ${align === 'between' ? 'justify-between' : ''}`}
    >
      {label && (
        <span className="text-[12.5px] font-semibold uppercase tracking-[0.14em] text-slate-400">
          {label}
        </span>
      )}

      <div className="flex flex-wrap items-center gap-x-5 gap-y-3 text-[13.5px] font-semibold">
        {set.has('whatsapp') && (
          <a
            href={`https://wa.me/393520526856?text=${waMsg}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setLeadSource(`${source}-whatsapp`)}
            data-testid={`pv-section-actions-${source}-whatsapp`}
            className="group inline-flex items-center gap-1.5 text-slate-700 hover:text-[#25D366] transition-colors"
          >
            <MessageCircle className="w-3.5 h-3.5" />
            <span className="border-b border-slate-300 group-hover:border-[#25D366] transition-colors pb-0.5">
              {txt.whatsapp}
            </span>
            <ArrowUpRight className="w-3 h-3 opacity-60 group-hover:opacity-100 transition-opacity" />
          </a>
        )}
        {set.has('brochure') && (
          <a
            href="/brochure"
            onClick={() => setLeadSource(`${source}-brochure`)}
            data-testid={`pv-section-actions-${source}-brochure`}
            className="group inline-flex items-center gap-1.5 text-slate-700 hover:text-pv-orange transition-colors"
          >
            <FileText className="w-3.5 h-3.5" />
            <span className="border-b border-slate-300 group-hover:border-pv-orange transition-colors pb-0.5">
              {txt.brochure}
            </span>
            <ArrowUpRight className="w-3 h-3 opacity-60 group-hover:opacity-100 transition-opacity" />
          </a>
        )}
        {set.has('demo') && (
          <a
            href="/bookacall"
            onClick={() => setLeadSource(`${source}-demo`)}
            data-testid={`pv-section-actions-${source}-demo`}
            className="group inline-flex items-center gap-1.5 text-slate-700 hover:text-pv-teal transition-colors"
          >
            <CalendarClock className="w-3.5 h-3.5" />
            <span className="border-b border-slate-300 group-hover:border-pv-teal transition-colors pb-0.5">
              {txt.demo}
            </span>
            <ArrowUpRight className="w-3 h-3 opacity-60 group-hover:opacity-100 transition-opacity" />
          </a>
        )}
      </div>
    </div>
  );
};

export default SectionActions;
